// src/app/router.jsx
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import AppShell from '@app/AppShell';
import { NotificationProvider } from './notifications/NotificationProvider';

// Lazy-loaded pages
const SignInPage = lazy(() => import('@pages/auth/Login'));
const ActivatePage = lazy(() => import('@pages/auth/Activate'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const HomePage = lazy(() => import('@pages/HomePage'));
const VendorsListPage = lazy(() => import('@pages/vendors/VendorsListPage'));
const CreateVendorPage = lazy(() => import('@pages/vendors/CreateVendorPage'));
const DerbyStatusManagementPage = lazy(() => import('@pages/vendors/DerbyStatusManagementPage'));
const EmployeesListPage = lazy(() => import('@pages/employees/EmployeeListPage'));
const EmployeeImportPage = lazy(() => import('@pages/employees/EmployeeImportPage'));
const HotelSearchPage = lazy(() => import('@pages/bookings/HotelSearchPage'));
const HotelDetailPage = lazy(() => import('@pages/bookings/HotelDetailPage'));
const BookingGuestDetailsPage = lazy(() => import('@pages/bookings/BookingGuestDetailsPage'));
const BookingConfirmationPage = lazy(() => import('@pages/bookings/BookingConfirmationPage'));
const BookingFinalConfirmationPage = lazy(() => import('@pages/bookings/BookingFinalConfirmationPage'));
const BookingPaymentPage = lazy(() => import('@pages/bookings/BookingPaymentPage'));
const MyReservationsPage = lazy(() => import('@pages/bookings/MyReservationsPage'));
const CustomerFeesPage = lazy(() => import('@pages/fees/CustomerFeesPage'));
const CustomersPage = lazy(() => import('@pages/customers/CustomersPage'));
const CustomerDetailPage = lazy(() => import('@pages/customers/CustomerDetailPage'));
const ChainsPage = lazy(() => import('@pages/chains/ChainsPage'));
const ProfilePage = lazy(() => import('@pages/users/ProfilePage'));
const UserManagement = lazy(() => import('../features/users/components/UserManagement'));
const BillingPage = lazy(() => import('@pages/billing/BillingPage'));
const BillingBatchDetailPage = lazy(() => import('@pages/billing/BillingBatchDetailPage'));
const BillingBatchesPage = lazy(() => import('@pages/billing/BillingBatchesPage'));
const BilledReservationsPage = lazy(() => import('@pages/billing/BilledReservationsPage'));
const BilledReservationDetailPage = lazy(() => import('@pages/billing/BilledReservationDetailPage'));
const RecordPaymentPage = lazy(() => import('@pages/billing/RecordPaymentPage'));
const RecordMultipleBatchPaymentPage = lazy(() => import('@pages/billing/RecordMultipleBatchPaymentPage'));
const PaymentExceptionsPage = lazy(() => import('@pages/billing/PaymentExceptionsPage'));
const PaymentsPage = lazy(() => import('@pages/billing/PaymentsPage'));
const StaffReservationsPage = lazy(() => import('@pages/staff-reservations/StaffReservationsPage'));
const CreateManualReservationPage = lazy(() => import('@pages/staff-reservations/CreateManualReservationPage'));
const VccCardsListPage = lazy(() => import('@pages/vcc-cards/VccCardsListPage'));
const IntegrationsPage = lazy(() => import('@pages/integrations/IntegrationsPage'));
const PoliciesPage = lazy(() => import('@pages/policies/PoliciesPage'));
const TagsPage = lazy(() => import('@pages/admin/TagsPage'));
const TagDetailPage = lazy(() => import('@pages/admin/TagDetailPage'));
const PolicyReviewsPage = lazy(() => import('@pages/policy-reviews/PolicyReviewsPage'));
import AuthProvider, { useAuth } from '@app/providers/AuthProvider';
import { BookingFlowProvider } from '@features/bookings/context/BookingFlowContext';

const PageLoader = () => <div style={{ padding: '2rem', textAlign: 'center' }}>Loading…</div>;
const withSuspense = (Component) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

// --- wrappers ---
function WithAuthProvider() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Outlet />
      </NotificationProvider>
    </AuthProvider>
  );
}

function RequirePermission({ ability }) {
  const { user } = useAuth();
  const has = !!user?.roles?.some((r) =>
    r?.permissions?.some((p) => p?.name === ability)
  );
  return has ? <Outlet /> : <Navigate to="/user-home" replace />;
}

// Role-based gate. Use when a route is restricted to specific role names
// (e.g. "only MSI Staff / Admin / Super Admin can create manual reservations")
// rather than a granular permission. Travel Managers fail this check.
function RequireRole({ roles }) {
  const { user } = useAuth();
  const has = !!user?.roles?.some((r) => roles.includes(r?.name));
  return has ? <Outlet /> : <Navigate to="/user-home" replace />;
}

function Protected() {
  const { user, checking } = useAuth();
  if (checking) return null;
  return user ? <Outlet /> : <Navigate to="/login" replace />;
}

function BookingFlowProviderOutlet() {
  return (
    <BookingFlowProvider>
      <Outlet />
    </BookingFlowProvider>
  );
}

// --- router ---
const routes = [
  {
    element: <WithAuthProvider />,
    children: [
      { path: '/login', element: withSuspense(SignInPage) },
      { path: '/activate', element: withSuspense(ActivatePage) },
      { path: '/reset-password', element: withSuspense(ResetPasswordPage) },

      {
        element: <Protected />,
        children: [
          {
            path: '/',
            element: <AppShell />,
            children: [
              {
                path: 'user-home',
                element: withSuspense(HomePage),
                handle: { crumb: 'Home', title: 'Home' },
              },
              {
                path: 'profile',
                element: withSuspense(ProfilePage),
                handle: { crumb: 'Profile', title: 'Profile Overview' },
              },
              {
                element: <RequirePermission ability="book reservations" />,
                children: [
                  {
                    path: 'reservations',
                    element: withSuspense(MyReservationsPage),
                    handle: { crumb: 'Reservations', title: 'My Reservations' },
                  },
                ],
              },
       
              {
                element: <RequirePermission ability="view users" />,
                children: [
                  {
                    path: 'users',
                    element: withSuspense(UserManagement),
                    handle: { crumb: 'User Management', title: 'User Management' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="manage hotel chains" />,
                children: [
                  {
                    path: 'chains',
                    element: withSuspense(ChainsPage),
                    handle: { crumb: 'Hotel Chains', title: 'Hotel Chains' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view vendors" />,
                children: [
                  {
                    path: 'vendors',
                    element: withSuspense(VendorsListPage),
                    handle: { crumb: 'Vendors', title: 'Vendors' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view customers" />,
                children: [
                  {
                    path: 'customers',
                    element: withSuspense(CustomersPage),
                    handle: { crumb: 'Customers', title: 'Customers' },
                  },
                  {
                    path: 'customers/:id',
                    element: withSuspense(CustomerDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Customers', to: '/customers' },
                        { label: 'Customer' },
                      ],
                      title: 'Customer',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="create vendors" />,
                children: [
                  {
                    path: 'vendors/create',
                    element: withSuspense(CreateVendorPage),
                    handle: {
                      crumbs: [
                        { label: 'Vendors', to: '/vendors' },
                        { label: 'Create Vendor' },
                      ],
                      title: 'Create Vendor',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="edit vendors" />,
                children: [
                  {
                    path: 'vendors/derby-status',
                    element: withSuspense(DerbyStatusManagementPage),
                    handle: {
                      crumbs: [
                        { label: 'Vendors', to: '/vendors' },
                        { label: 'Derby Status' },
                      ],
                      title: 'Derby Status',
                    },
                  },
                  {
                    path: 'vendors/:id/edit',
                    element: withSuspense(CreateVendorPage),
                    handle: {
                      crumbs: [
                        { label: 'Vendors', to: '/vendors' },
                        { label: 'Edit Vendor' },
                      ],
                      title: 'Edit Vendor',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view transactions" />,
                children: [
                  // The /billing root is the MSI "generate batch from
                  // candidates" dashboard — calls /api/billing/candidates
                  // which requires `manage billing`. Block TMs at the route
                  // level so they don't hit the 403 toast.
                  {
                    element: <RequireRole roles={['MSI Staff', 'Admin', 'Super Admin']} />,
                    children: [
                      {
                        path: 'billing',
                        element: withSuspense(BillingPage),
                        handle: { crumb: 'Billing', title: 'Billing Batches' },
                      },
                    ],
                  },
                  {
                    path: 'billing/batches',
                    element: withSuspense(BillingBatchesPage),
                    handle: { crumb: 'Batches', title: 'All Batches' },
                  },
                  {
                    path: 'billing/payments',
                    element: withSuspense(PaymentsPage),
                    handle: { crumb: 'Payment History', title: 'Payment History' },
                  },
                  {
                    path: 'billing/:id',
                    element: withSuspense(BillingBatchDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Billing', to: '/billing' },
                        { label: 'Batch Detail' },
                      ],
                      title: 'Billing Batch',
                    },
                  },
                  // MSI-only billing routes — the /billing dashboard (which
                  // is actually the "generate batch from candidates"
                  // workflow), write actions, and the Billed Reservations
                  // investigation tool. Travel Managers navigating to these
                  // URLs are redirected to /user-home. Backend also blocks
                  // via the `manage billing` permission for writes; this is
                  // the client-side equivalent.
                  {
                    element: <RequireRole roles={['MSI Staff', 'Admin', 'Super Admin']} />,
                    children: [
                      {
                        path: 'billing/record-payment',
                        element: withSuspense(RecordPaymentPage),
                        handle: { crumb: 'Record Payment', title: 'Record Payment' },
                      },
                      {
                        path: 'billing/record-payment/process',
                        element: withSuspense(RecordMultipleBatchPaymentPage),
                        handle: { crumb: 'Process Payment', title: 'Process Payment' },
                      },
                      {
                        path: 'billing/billed-reservations',
                        element: withSuspense(BilledReservationsPage),
                        handle: {
                          crumbs: [
                            { label: 'Billing', to: '/billing' },
                            { label: 'Billed Reservations' },
                          ],
                          title: 'Billed Reservations',
                        },
                      },
                      {
                        path: 'billing/billed-reservations/:reservationId',
                        element: withSuspense(BilledReservationDetailPage),
                        handle: {
                          crumbs: [
                            { label: 'Billing', to: '/billing' },
                            { label: 'Billed Reservations', to: '/billing/billed-reservations' },
                            { label: 'Reservation Ledger' },
                          ],
                          title: 'Reservation Ledger',
                        },
                      },
                    ],
                  },
                ],
              },
              {
                element: <RequirePermission ability="view employees" />,
                children: [
                  {
                    path: 'employees',
                    element: withSuspense(EmployeesListPage),
                    handle: { crumb: 'Employees', title: 'Employees' },
                  },
                  {
                    path: 'staff-reservations',
                    element: withSuspense(StaffReservationsPage),
                    handle: { crumb: 'Reservations', title: 'Reservations' },
                  },
                  // Manual reservation create/edit + standalone VCC issuance
                  // are MSI-only. Travel Managers can VIEW the
                  // staff-reservations list (scoped to their customer) but
                  // cannot create or edit manual entries or issue cards.
                  // Backend enforces this too (assertMsiStaff in controller),
                  // but this prevents the page from even loading.
                  {
                    element: <RequireRole roles={['MSI Staff', 'Admin', 'Super Admin']} />,
                    children: [
                      {
                        path: 'staff-reservations/create',
                        element: withSuspense(CreateManualReservationPage),
                        handle: { crumb: 'Create Reservation', title: 'Create Manual Reservation' },
                      },
                      {
                        path: 'staff-reservations/:id/edit',
                        element: withSuspense(CreateManualReservationPage),
                        handle: { crumb: 'Edit Reservation', title: 'Edit Manual Reservation' },
                      },
                      {
                        path: 'vcc-cards',
                        element: withSuspense(VccCardsListPage),
                        handle: { crumb: 'VCCs', title: 'VCCs' },
                      },
                    ],
                  },
                ],
              },
              {
                // Bulk CSV import is admin-only. TMs can add employees one
                // at a time via the Add Employee modal but cannot mass-
                // onboard. Backend enforces the same gate via the
                // `bulk import employees` permission on the import routes.
                element: <RequirePermission ability="bulk import employees" />,
                children: [
                  {
                    path: 'employees/imports',
                    element: withSuspense(EmployeeImportPage),
                    handle: {
                      crumbs: [
                        { label: 'Employees', to: '/employees' },
                        { label: 'Import Employees' },
                      ],
                      title: 'Import Employees',
                    },
                  },
                ],
              },
              {
                element: <RequirePermission ability="manage customer fees" />,
                children: [
                  {
                    path: 'customer-fees',
                    element: withSuspense(CustomerFeesPage),
                    handle: { crumb: 'Customer Fees', title: 'Customer Fees' },
                  },
                ],
              },
              {
                // Integrations UI is Admin-only via the narrower `manage
                // integrations` permission. It was previously grouped under
                // `view transactions` alongside payment-exceptions, but that
                // permission is also held by Travel Managers (for billing
                // reads) — grouping them together let TMs into the QuickBooks
                // controls, which is what F-1 in the review flagged.
                element: <RequirePermission ability="manage integrations" />,
                children: [
                  {
                    path: 'integrations',
                    element: withSuspense(IntegrationsPage),
                    handle: { crumb: 'Integrations', title: 'Integrations' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view transactions" />,
                children: [
                  {
                    path: 'payment-exceptions',
                    element: withSuspense(PaymentExceptionsPage),
                    handle: { crumb: 'Payment Exceptions', title: 'Payment Exceptions' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view policies" />,
                children: [
                  {
                    path: 'policies',
                    element: withSuspense(PoliciesPage),
                    handle: { crumb: 'Policies', title: 'Policies' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view tags" />,
                children: [
                  {
                    path: 'tags',
                    element: withSuspense(TagsPage),
                    handle: { crumb: 'Tags', title: 'Tags' },
                  },
                  {
                    path: 'tags/:id',
                    element: withSuspense(TagDetailPage),
                    handle: { crumb: 'Tag', title: 'Tag detail' },
                  },
                ],
              },
              {
                element: <RequirePermission ability="view policy reviews" />,
                children: [
                  {
                    path: 'policy-reviews',
                    element: withSuspense(PolicyReviewsPage),
                    handle: { crumb: 'Policy Reviews', title: 'Policy Reviews' },
                  },
                ],
              },
              { index: true, element: <Navigate to="user-home" replace /> },
              {
                element: <BookingFlowProviderOutlet />,
                children: [
                  {
                    path: 'booking',
                    element: withSuspense(HotelSearchPage),
                    handle: { crumb: 'Reservation', title: 'Reservation Search' },
                  },
                  {
                    path: 'booking/:hotelId',
                    element: withSuspense(HotelDetailPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Hotel Details' },
                      ],
                      title: 'Hotel Details',
                    },
                  },
                  {
                    path: 'booking/:hotelId/guests',
                    element: withSuspense(BookingGuestDetailsPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Guest Details' },
                      ],
                      title: 'Guest Details',
                    },
                  },
                  {
                    path: 'booking/confirmation/:confirmationId',
                    element: withSuspense(BookingFinalConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Confirmation' },
                      ],
                      title: 'Confirmation',
                    },
                  },
                  {
                    path: 'booking/:hotelId/confirmation',
                    element: withSuspense(BookingConfirmationPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Confirm Reservation' },
                      ],
                      title: 'Confirm Reservation',
                    },
                  },
                  {
                    path: 'booking/:hotelId/payment',
                    element: withSuspense(BookingPaymentPage),
                    handle: {
                      crumbs: [
                        { label: 'Reservation', to: '/booking' },
                        { label: 'Payment' },
                      ],
                      title: 'Payment',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
export { routes };
